import type { GeoDirectoryStore } from "../data/geoDirectoryStore";
import type { TelanganaDistrictKey } from "../data/telanganaDistricts";
import { isTelanganaDistrictKey } from "../data/telanganaDistricts";
import { bestFuzzyMatch } from "./fuzzyGeoMatch";
import { normalizeGeoLabel } from "./geoLabel";
import type { ParsedLeadRow } from "./leadsImportParse";

/** Minimum similarity (0–1) to accept a fuzzy village match when no exact/substring hit. */
const FUZZY_VILLAGE_MIN_SCORE = 0.66;

export type MatchedLeadRow = ParsedLeadRow & {
  districtKey: TelanganaDistrictKey;
  canonicalMandal: string;
  canonicalVillage: string;
  /** True when village was resolved by fuzzy similarity, not exact directory name. */
  villageMatchedFuzzy?: boolean;
};

export type UnmatchedLeadRow = ParsedLeadRow & {
  reason: string;
};

export function resolveMandalInDistrict(
  store: GeoDirectoryStore,
  districtKey: TelanganaDistrictKey,
  fileMandal: string,
): string | null {
  const nm = normalizeGeoLabel(fileMandal);
  const m = store.tgMandalsForDistrict(districtKey).find((x) => normalizeGeoLabel(x.mandalName) === nm);
  return m?.mandalName ?? null;
}

/** Pick canonical village name from IGRS directory list for the mandal (not map-based). */
export function matchVillageFromDirectoryList(
  store: GeoDirectoryStore,
  districtKey: TelanganaDistrictKey,
  canonicalMandal: string,
  fileVillage: string,
): { name: string; fuzzy: boolean } | null {
  const villages = store.tgVillagesForMandal(districtKey, canonicalMandal);
  if (!villages.length) return null;
  const fv = normalizeGeoLabel(fileVillage);
  const exact = villages.find((v) => normalizeGeoLabel(v.name) === fv);
  if (exact) return { name: exact.name, fuzzy: false };
  const starts = villages.find((v) => normalizeGeoLabel(v.name).startsWith(fv) || fv.startsWith(normalizeGeoLabel(v.name)));
  if (starts) return { name: starts.name, fuzzy: false };
  const includes = villages.find(
    (v) =>
      normalizeGeoLabel(v.name).includes(fv) ||
      fv.includes(normalizeGeoLabel(v.name)) ||
      normalizeGeoLabel(v.name).replace(/\s+/g, "") === fv.replace(/\s+/g, ""),
  );
  if (includes) return { name: includes.name, fuzzy: false };
  const fuzzy = bestFuzzyMatch(fileVillage, villages, (v) => v.name, FUZZY_VILLAGE_MIN_SCORE);
  if (fuzzy) return { name: fuzzy.item.name, fuzzy: true };
  return null;
}

export function matchParsedLeadsToGeo(
  store: GeoDirectoryStore | null,
  districtKey: TelanganaDistrictKey | null,
  parsed: ParsedLeadRow[],
): { matched: MatchedLeadRow[]; unmatched: UnmatchedLeadRow[] } {
  const matched: MatchedLeadRow[] = [];
  const unmatched: UnmatchedLeadRow[] = [];

  if (!store) {
    for (const r of parsed) {
      unmatched.push({ ...r, reason: "Geo directory not loaded." });
    }
    return { matched, unmatched };
  }

  for (const r of parsed) {
    const dKey: TelanganaDistrictKey | null =
      r.fileDistrictKey != null && isTelanganaDistrictKey(r.fileDistrictKey)
        ? r.fileDistrictKey
        : districtKey && isTelanganaDistrictKey(districtKey)
          ? districtKey
          : null;
    if (!dKey) {
      unmatched.push({
        ...r,
        reason:
          "Missing or unknown district for this row. Use a District column in Excel, or a “District - …” line in PDF exports.",
      });
      continue;
    }

    const mandal = resolveMandalInDistrict(store, dKey, r.fileMandal);
    if (!mandal) {
      unmatched.push({ ...r, reason: `Mandal "${r.fileMandal}" not found in ${dKey} directory.` });
      continue;
    }
    const villageHit = matchVillageFromDirectoryList(store, dKey, mandal, r.fileVillage);
    if (!villageHit) {
      unmatched.push({
        ...r,
        reason: `Village "${r.fileVillage}" not found under mandal "${mandal}" in directory list.`,
      });
      continue;
    }
    matched.push({
      ...r,
      districtKey: dKey,
      canonicalMandal: mandal,
      canonicalVillage: villageHit.name,
      villageMatchedFuzzy: villageHit.fuzzy,
    });
  }

  return { matched, unmatched };
}
